import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Link2Off, Loader2, WifiOff } from "lucide-react";
import betaAPI from "v2/api";
import V2AuthButton from "v2/views/auth/components/V2AuthButton";
import GuardianSignup from "./GuardianSignup";
import PlayerSignup from "./PlayerSignup";
import { StatusCard } from "./JoinLayout";

/**
 * Landing page for a team invite link (/v2/join/:token). The API resolves the
 * token to the team and says which sign-up to show (guardian for under-16
 * teams); this page never decides that itself.
 */
export default function JoinTeam() {
  const { t } = useTranslation();
  const { token } = useParams();
  const [state, setState] = useState({ status: "loading" });

  const resolve = useCallback(async () => {
    setState({ status: "loading" });
    try {
      const { data } = await betaAPI.resolveTeamInvite({ token });
      setState({ status: "ready", invite: data });
    } catch (err) {
      if (err.status === 404 || err.status === 410) {
        setState({
          status: "unavailable",
          reason: err.payload?.reason || "invalid",
        });
      } else {
        console.error("Failed to resolve team invite", err);
        setState({ status: "error" });
      }
    }
  }, [token]);

  useEffect(() => {
    resolve();
  }, [resolve]);

  if (state.status === "loading") {
    return (
      <StatusCard>
        <div className="flex flex-col items-center py-8">
          <Loader2 size={32} className="animate-spin text-[#E07A3A]" />
          <p className="mt-4 text-sm text-[#6B7280]">
            {t("auth.joinTeam.loading")}
          </p>
        </div>
      </StatusCard>
    );
  }

  if (state.status === "unavailable") {
    return (
      <StatusCard>
        <div className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-[#FEF2F2] text-[#DC2626]">
            <Link2Off size={22} />
          </div>
          <h1 className="mb-2 text-xl font-bold text-[#111827]">
            {state.reason === "invalid"
              ? t("auth.joinTeam.invalidTitle")
              : t("auth.joinTeam.inactiveTitle")}
          </h1>
          <p className="text-[15px] leading-relaxed text-[#6B7280]">
            {t("auth.joinTeam.askCoach")}
          </p>
        </div>
      </StatusCard>
    );
  }

  if (state.status === "error") {
    return (
      <StatusCard>
        <div className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-[#F3F4F6] text-[#6B7280]">
            <WifiOff size={22} />
          </div>
          <h1 className="mb-2 text-xl font-bold text-[#111827]">
            {t("auth.joinTeam.errorTitle")}
          </h1>
          <p className="mb-6 text-[15px] text-[#6B7280]">
            {t("auth.joinTeam.errorBody")}
          </p>
          <V2AuthButton variant="secondary" onClick={resolve}>
            {t("common.actions.tryAgain")}
          </V2AuthButton>
        </div>
      </StatusCard>
    );
  }

  const Signup =
    state.invite.signup === "guardian" ? GuardianSignup : PlayerSignup;
  return <Signup invite={state.invite} token={token} />;
}
